import React from 'react';
import { Navigate, Link, useNavigate, useParams } from 'react-router-dom';
import { FaArrowRight, FaGasPump, FaMapMarkedAlt, FaRoad, FaWallet } from 'react-icons/fa';
import Header from '../components/Common/Header';
import Seo from '../components/Common/Seo';
import { routeGuideMap, routeGuides } from '../data/routeGuides';
import '../css/route-guide.css';

const GuideMetric = ({ icon, label, value }) => (
  <div className="guide-metric">
    <div className="guide-metric-icon">{icon}</div>
    <div>
      <strong>{label}</strong>
      <span>{value}</span>
    </div>
  </div>
);

const RouteGuidePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const guide = routeGuideMap[slug];

  if (!guide) {
    return <Navigate to="/" replace />;
  }

  const relatedGuides = routeGuides.filter((item) => item.slug !== guide.slug);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: guide.title,
        description: guide.description,
        mainEntityOfPage: `https://www.whe2.com${guide.path}`,
        author: {
          '@type': 'Organization',
          name: 'Whe2'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Whe2'
        }
      },
      {
        '@type': 'FAQPage',
        mainEntity: guide.faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      }
    ]
  };

  return (
    <div className="route-guide-page">
      <Seo
        title={guide.seoTitle}
        description={guide.description}
        path={guide.path}
        type="article"
        jsonLd={jsonLd}
      />
      <Header />
      <main className="route-guide-main">
        <div className="route-guide-shell">
          <nav className="guide-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>{guide.shortTitle}</span>
          </nav>

          <section className="guide-hero">
            <p className="guide-kicker">South Africa route guide</p>
            <h1>{guide.title}</h1>
            <p className="guide-lead">{guide.intro}</p>
            <div className="guide-metrics">
              <GuideMetric icon={<FaRoad />} label="Distance" value={guide.distance} />
              <GuideMetric icon={<FaMapMarkedAlt />} label="Drive time" value={guide.duration} />
              <GuideMetric icon={<FaWallet />} label="Tolls" value={guide.tolls} />
              <GuideMetric icon={<FaGasPump />} label="Fuel" value={guide.fuel} />
            </div>
          </section>

          <section className="guide-content-grid">
            <article className="guide-card">
              <h2>Why this route matters</h2>
              <ul>
                {guide.whyItMatters.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>

            <article className="guide-card">
              <h2>How to plan it better</h2>
              <ul>
                {guide.planningPoints.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </section>

          <section className="guide-card guide-card-wide">
            <h2>Use Whe2 to plan the route properly</h2>
            <p>
              Whe2 helps you compare South African route options with fuel cost estimates, toll costs,
              travel time and total trip cost in the app. Instead of guessing, you can set your
              vehicle details and compare the trade-off between time and cost before you leave.
            </p>
            <div className="guide-cta-row">
              <button
                type="button"
                className="guide-primary-cta"
                onClick={() => navigate('/', { state: { scrollTarget: 'download' } })}
              >
                Download Whe2 <FaArrowRight />
              </button>
            </div>
          </section>

          {guide.faqs?.length ? (
            <section className="guide-card guide-card-wide">
              <h2>Frequently asked questions</h2>
              <div className="guide-faq-list">
                {guide.faqs.map((item) => (
                  <article key={item.question} className="guide-faq-item">
                    <h3>{item.question}</h3>
                    <p>{item.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {relatedGuides.length ? (
            <section className="guide-card guide-card-wide">
              <h2>Related South Africa route guides</h2>
              <div className="guide-related-grid">
                {relatedGuides.map((item) => (
                  <Link key={item.slug} className="guide-related-link" to={item.path}>
                    <strong>{item.shortTitle}</strong>
                    <span>{item.description}</span>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default RouteGuidePage;
